import React, { useState, useRef, useEffect } from 'react';
import { useDropzone } from 'react-dropzone';

import type { IChatInputProps } from './ChatInput.types';
import { Document } from '../DocumentsPanel/DocumentsPanel.types';
import { Button } from '../Button/Button';
import { Icon } from '../Icon/Icon';
import { IconName } from '@ui-library/Icon/Icon.type';
import { FileTypeIcon } from '../DocumentsPanel/components/FileTypeIcon';
import { ModelSelector, ModelPreference } from '../ModelSelector';

import api from '../../utils/axios';
import { formatArtifactTitle } from '../../utils/parseArtifacts';
import { 
  MAX_IMAGE_SIZE,
  IMAGE_MIME_TYPES,
  DOCUMENT_MIME_TYPES,
  isImageFile,
  isDocumentFile
} from '../../utils/fileUploadConfig';

import { FileUploadDialog } from '../Dialogs/FileUploadDialog';

const FilePreview = ({ files, onRemove }: { files: Document[]; onRemove: (id: string) => void }) => {
  return (
    <div className="flex flex-wrap gap-bg-v2-sm mb-bg-v2-md">
      {files.map(file => (
        <div key={file.id} className="relative group">
          <div className="flex items-center gap-bg-v2-xs rounded-bg-v2-md px-bg-v2-md py-bg-v2-sm bg-bg-secondary border border-border-secondary">
            <FileTypeIcon extension={file.fileType} size={16} />
            <span className="text-bg-v2-xs font-bg-v2-medium text-text-primary max-w-[120px] truncate">{file.source === 'artifact' ? formatArtifactTitle(file.name, file.version) : file.name}</span>
            <button
              onClick={() => onRemove(file.id)}
              className="ml-bg-v2-xs text-text-tertiary hover:text-text-error transition-colors">
              <Icon name={IconName.Close} size="xs" variant="secondary" />
            </button>
          </div>
        </div>
      ))}
    </div>
  );
};

export const ChatInput: React.FC<IChatInputProps> = ({
  value: controlledValue,
  onChange,
  onSend,
  onStop,
  placeholder = 'Type your message...',
  disabled = false,
  isStreaming = false,
  isSending = false,
  className = '',
  projectId = '',
  showAttachmentButton = true,
  showModelSelector = false,
  model: controlledModel,
  onModelChange,
}) => {
  const [internalValue, setInternalValue] = useState('');
  const [allDocuments, setAllDocuments] = useState<Document[]>([]);
  const [showFileUpoloadDialog, setShowFileUpoloadDialog] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [isDragOverTextarea, setIsDragOverTextarea] = useState(false);
  const [uploadingFiles, setUploadingFiles] = useState<string[]>([]);
  const [internalModel, setInternalModel] = useState<ModelPreference>(ModelPreference.MEDIUM);
  
  const isModelControlled = controlledModel !== undefined;
  const model = isModelControlled ? controlledModel : internalModel;

  const MODEL_PREFERENCES = [
    { value: ModelPreference.MEDIUM, label: 'Medium', description: 'Balanced speed & quality' },
    { value: ModelPreference.HIGH, label: 'High', description: 'Best quality output' },
  ];

  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const uploadAbortController = useRef<AbortController | null>(null);
  const isControlled = controlledValue !== undefined;
  const value = isControlled ? controlledValue : internalValue;

  // Auto-resize textarea
  useEffect(() => {
    const textarea = textareaRef.current;
    if (textarea) {
      // Reset height to auto to get the correct scrollHeight
      textarea.style.height = 'auto';
      // Set height to scrollHeight (content height) but respect max-height
      const newHeight = Math.min(textarea.scrollHeight, 200); // 200px max height
      textarea.style.height = `${newHeight}px`;
    }
  }, [value]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop: async (acceptedFiles, rejectedFiles) => {
      setIsDragOverTextarea(false);
      
      if (acceptedFiles.length > 0) {
        await handleFileUpload(acceptedFiles);
      } else if (rejectedFiles.length > 0) {
        // Try to upload rejected files anyway (we'll validate in handleFileUpload)
        const rejectedFileObjects = rejectedFiles.map(fr => fr.file);
        await handleFileUpload(rejectedFileObjects);
      }
    },
    onDragEnter: () => {
      setIsDragOverTextarea(true);
    },
    onDragLeave: () => {
      setIsDragOverTextarea(false);
    },
    noClick: true,
    noKeyboard: true,
    multiple: true,
    validator: (file) => {
      // Accept all files - we'll filter in handleFileUpload
      return null;
    }
  });

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const newValue = e.target.value;
    if (!isControlled) {
      setInternalValue(newValue);
    }
    onChange?.(newValue);
  };

  const handleSend = async () => {
    if (!onSend) return;

    const hasContent = value.trim() || allDocuments.length > 0;    
    if (!hasContent) return;

    const attachments = allDocuments.map(doc => ({
      id: doc.id,
      name: doc.source === 'artifact' ? formatArtifactTitle(doc.name, doc.version) : doc.name,
      source: doc.source,
      type: (doc.source === 'artifact' || doc.source === 'cab_markdown') ? 'system' as const : 'user' as const,
    }));

    onSend(value.trim(), attachments, model);

    if (!isControlled) {
      setInternalValue('');
    }
    setAllDocuments([]);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };


  // Paste handler for textarea
  const handleTextareaPaste = async (e: React.ClipboardEvent) => {
    const items = e.clipboardData?.items;
    if (!items) return;
    
    const pastedFiles: File[] = [];
    
    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      // Handle both images and files
      if (item.kind === 'file') {
        const file = item.getAsFile();
        if (file) {
          pastedFiles.push(file);
        }
      }
    }
    
    if (pastedFiles.length > 0) {
      e.preventDefault(); // Prevent default paste behavior for files
      await handleFileUpload(pastedFiles);
    }
  };

  const handleFileUpload = async (files: File[] | FileList) => {
    if (files instanceof FileList) {
      files = Array.from(files);
    };

    const images = files.filter(file => isImageFile(file));
    const documents = files.filter(file => isDocumentFile(file));
    
    try {
      setUploading(true);
      setUploadingFiles(files.map(f => f.name));
      
      // Create new AbortController for this upload
      uploadAbortController.current = new AbortController();
      
      const uploadPromises = [
        ...images.map(image => {
          const formData = new FormData();
          formData.append('file', image);
          return api.post(`/projects/${projectId}/images/`, formData, {
            headers: {
              'Content-Type': 'multipart/form-data',
              'Authorization': `Bearer ${localStorage.getItem('token')}`
            },
            signal: uploadAbortController.current?.signal
          }).then(res => ({ ...res, uploadType: 'image' }));
        }),
        ...documents.map(document => {
          const formData = new FormData();
          formData.append('file', document);
          return api.post(`/projects/${projectId}/files/`, formData, {
            headers: {
              
              'Content-Type': 'multipart/form-data',
              'Authorization': `Bearer ${localStorage.getItem('token')}`
            },
            signal: uploadAbortController.current?.signal
          }).then(res => ({ ...res, uploadType: 'file' }));
        })
      ];
      
      const responses = await Promise.all(uploadPromises);

      // Add uploaded files to preview with proper source
      const uploadedDocs: Document[] = responses.map((res: any, idx) => ({
        id: res.data.id,
        name: res.data.original_name || res.data.filename || files[idx].name,
        size: res.data.size || files[idx].size,
        dateModified: res.data.uploaded_at || new Date().toISOString(),
        type: 'user' as const,
        fileType: res.data.original_name ? res.data.original_name.split('.').pop() || '' : '',
        source: res.uploadType // Set source based on upload type (image or file)
      }));
      setAllDocuments(prev => [...prev, ...uploadedDocs]);
      setUploading(false);
      setUploadingFiles([]);
      uploadAbortController.current = null;
      return uploadedDocs;
    } catch (error: any) {
      console.error('Upload error:', error);
      setUploading(false);
      setUploadingFiles([]);
      uploadAbortController.current = null;
      
      // Don't show error alert if upload was cancelled
      if (error.name !== 'CanceledError' && error.name !== 'AbortError') {
        alert('Failed to upload files. Please try again.');
      }
    }
  };

  const handleCancelUpload = () => {
    if (uploadAbortController.current) {
      uploadAbortController.current.abort();
      uploadAbortController.current = null;
    }
    setUploading(false);
    setUploadingFiles([]);
  };

  return (
    <div className={`flex flex-col w-full max-w-[800px] ${className}`} {...getRootProps()}>
      <input {...getInputProps()} />
      <div className="bg-bg-primary border border-border-secondary p-bg-v2-xl rounded-bg-v2-xl container-shadow-md relative">
        {allDocuments.length > 0 && (
          <FilePreview 
            files={allDocuments}
            onRemove={(id) => setAllDocuments(prev => prev.filter(doc => doc.id !== id))} 
          />
        )}
        <div className="relative">
          <textarea
            ref={textareaRef}
            value={value}
            onChange={handleChange}
            onKeyDown={handleKeyDown}
            onPaste={handleTextareaPaste}
            placeholder={placeholder}
            disabled={disabled || uploading}
            rows={1}
            className={`w-full min-h-[40px] max-h-[200px] resize-none text-bg-v2-sm leading-bg-v2-text-sm font-bg-v2-medium text-text-primary placeholder:text-text-placeholder bg-transparent border-none outline-none focus:ring-0 overflow-y-auto transition-all ${
              disabled || uploading ? 'cursor-not-allowed' : ''
            }`}
          />
        </div>
        <div className={`flex items-end mt-bg-v2-md ${(showAttachmentButton || showModelSelector) ? 'justify-between' : 'justify-end'}`}>
          <div className="flex gap-bg-v2-sm">
          {showAttachmentButton && (
            <div className="relative">
              <Button
                label={<Icon name={IconName.Attachment} size="sm" className="text-border-brand-icn" inheritColor />}
                size="small"
                variant='outline'
                className="!bg-bg-brand-primary-alt !rounded-bg-v2-full border-none !w-10 !h-10 !p-0 flex items-center justify-center"
                disabled={disabled || isStreaming || uploading}
                onClick={() => setShowFileUpoloadDialog(!showFileUpoloadDialog)}
              />
            </div>
          )}
          {showModelSelector && (
            <ModelSelector
              model={model}
              onChange={(newModel) => {
                if (isModelControlled && onModelChange) {
                  onModelChange(newModel);
                } else {
                  setInternalModel(newModel);
                }
              }}
              options={MODEL_PREFERENCES}
            />
          )}
          </div>
          {isStreaming ? (
            <Button
              label={<Icon name={IconName.Stop} size="lg" className={"bg-fg-primary"} inheritColor={true} />}
              size="medium"
              variant='outline'
              className="!bg-bg-secondary-hover !rounded-bg-v2-full border border-border-error hover:!bg-bg-error-primary !w-12 !h-12 !p-0 flex items-center justify-center"
              onClick={onStop}
            />
          ) : (
            <Button
              label={
                (uploading || isSending) ? (
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                ) : (
                  <Icon name={IconName.SendMessage} size="md" />
                )
              }
              size="medium"
              variant='outline'
              className="!bg-bg-brand-solid !rounded-bg-v2-full border-none !w-12 !h-12 !p-0 flex items-center justify-center"
              disabled={disabled || uploading || isSending}
              onClick={handleSend}
            />
          )}
        </div>

        {/* Drag Overlay */}
        {(isDragOverTextarea || isDragActive) && !uploading && (
          <div className="absolute inset-0 bg-bg-brand-primary border-2 border-dashed border-border-brand rounded-bg-v2-xl flex items-center justify-center pointer-events-none z-10">
            <div className="flex flex-col items-center gap-bg-v2-lg text-center px-bg-v2-md">
              <Icon name={IconName.Upload} size="lg" variant="brand-utility" />
              <div className="flex flex-col gap-bg-v2-xs">
                <span className="text-bg-v2-sm font-bg-v2-semibold text-text-brand-primary">
                  Drop files here
                </span>
                <span className="text-bg-v2-xs font-bg-v2-regular text-text-tertiary">
                  Images & Documents supported
                </span>
              </div>
            </div>
          </div>
        )}

        {/* Upload Progress Overlay */}
        {uploading && uploadingFiles.length > 0 && (
          <div className="absolute inset-0 bg-bg-primary/95 border-2 border-border-brand rounded-bg-v2-xl flex items-center justify-center z-20">
            <div className="flex flex-col items-center gap-bg-v2-lg text-center px-bg-v2-md max-w-full">
              <div className="animate-spin rounded-full w-bg-v2-2xl h-bg-v2-2xl border-2 border-t-transparent border-fg-brand-primary"></div>
              <div className="flex flex-col gap-bg-v2-xs">
                <span className="text-bg-v2-sm font-bg-v2-semibold text-text-primary">
                  Uploading in progress...
                </span>
                <div className="flex flex-col gap-bg-v2-xxs mt-bg-v2-sm">
                  {uploadingFiles.map((fileName, index) => (
                    <span 
                      key={index} 
                      className="text-bg-v2-xs font-bg-v2-regular text-text-tertiary truncate max-w-full"
                    >
                      {fileName}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
      
      {showAttachmentButton && (
        <FileUploadDialog
          projectId={projectId}
          isOpen={showFileUpoloadDialog}
          onClose={() => setShowFileUpoloadDialog(false)}
          onSelect={(files) => {
            // Only add files that are not already in allDocuments (to prevent duplicates)
            setAllDocuments(prev => {
              const existingIds = new Set(prev.map(doc => doc.id));
              const newFiles = files.filter(file => !existingIds.has(file.id));
              return [...prev, ...newFiles];
            });
            setShowFileUpoloadDialog(false);
          }}
          onUpload={async (files) => {
            const uploadedDocs = await handleFileUpload(files);
            return uploadedDocs || [];
          }}
          uploading={uploading}
          onCancelUpload={handleCancelUpload}
          onCancel={(uploadedDocumentIds) => {
            // Remove uploaded documents from this session
            setAllDocuments(prev => prev.filter(doc => !uploadedDocumentIds.includes(doc.id)));
          }}
        />
      )}
    </div>
  );
};